import { SimpleRenderer } from "@arcgis/core/renderers";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import { MapProps } from "./EsriMap";
import Graphic from "@arcgis/core/Graphic";
import { locationToAddress } from "@arcgis/core/rest/locator";
import axios from "axios";
import SearchSource from "@arcgis/core/widgets/Search/SearchSource.js";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine.js";
import { Point } from "@arcgis/core/geometry.js";
import { WasteLocation, emptyLocation } from "@store/storeSlices/wasteDetails";
import * as Sentry from "@sentry/react";
import { isMobile } from "react-device-detect";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";

export const WCC_BOUNDARY_LAYER =
  "https://www.rbkc.gov.uk/arcgis/rest/services/WCC/FixMyStreetOpen/MapServer/4";

export const GEOCODE_SERVER = import.meta.env.MAINSITE_GEOCODE_SERVER;

export const initialCenterWCC = [-0.151843, 51.511422];

const LOCATION_ENDPOINT =
  import.meta.env.MAINSITE_WCC_APIM_BASE_URL + "/locations/v1/find";
const NEAREST_ENDPOINT =
  import.meta.env.MAINSITE_WCC_APIM_BASE_URL + "/locations/v1/nearest";
const apiKey = import.meta.env.MAINSITE_LOCATION_API_KEY;

export const boundaryLayer = new FeatureLayer({
  url: WCC_BOUNDARY_LAYER,
});

export const markerSymbol = {
  type: "simple-marker",
  path: "M16,3.5c-4.142,0-7.5,3.358-7.5,7.5c0,4.143,7.5,18.121,7.5,18.121S23.5,15.143,23.5,11C23.5,6.858,20.143,3.5,16,3.5z M16,14.584c-1.979,0-3.584-1.604-3.584-3.584S14.021,7.416,16,7.416S19.584,9.021,19.584,11S17.979,14.584,16,14.584z",
  size: "30px",
  color: "#0067DD",
  outline: {
    color: "white",
  },
};

export const renderer = new SimpleRenderer({
  symbol: new SimpleFillSymbol({
    color: "rgba(88, 151, 252, 0.15)",
    outline: {
      color: "rgba(0,76,115,0.80)",
      width: "2px",
    },
  }),
});

type layerQueryType = {
  geometry: {
    type: string;
    longitude: number;
    latitude: number;
  };
  returnGeometry: boolean;
};
export const layerQuery = (latlon: MapProps): layerQueryType => ({
  geometry: {
    type: "point",
    longitude: latlon![0],
    latitude: latlon![1],
  },
  returnGeometry: true,
});

export const pointGraphic = (
  coordinates: __esri.ViewClickEvent | NonNullable<MapProps>,
) => {
  let coords;

  if (Array.isArray(coordinates)) {
    coords = coordinates;
  } else {
    coords = [coordinates.mapPoint.longitude, coordinates.mapPoint.latitude];
  }
  return new Graphic({
    geometry: {
      // @ts-expect-error "type" is required in the geometry object but it is not in the type definition
      type: "point",
      longitude: coords[0],
      latitude: coords[1],
    },
    symbol: markerSymbol,
  });
};

export const dropMarkerOnMap = (
  mapView: __esri.MapView,
  marker: __esri.Graphic,
  address?: __esri.AddressCandidate | null,
) => {
  mapView.graphics.removeAll();
  mapView.graphics.add(marker);
  if (address !== null && address !== undefined) {
    mapView.popup.open({
      title: "Address",
      content: address.attributes.OfficialLAAddress,
      location: marker.geometry,
    });
  }
};

export const updateMapGivenLatLong = async (
  latitude: number,
  longitude: number,
  mapView: __esri.MapView,
  locationUpdate: (locationOfWaste: WasteLocation) => void,
) => {
  const insideBorough = await isCoordinatesInsideBorough([longitude, latitude]);
  const point = new Point({ x: longitude, y: latitude });
  if (insideBorough) {
    const pinAddress = await getAddressCandidateFromMapPoint(point);
    if (pinAddress !== null) {
      updateLocationAndDropMapMarker(
        mapView,
        [longitude, latitude],
        pinAddress,
        locationUpdate,
      );
    }
  }
};

export const updateLocationAndDropMapMarker = (
  mapView: __esri.MapView,
  coordinates: __esri.ViewClickEvent | NonNullable<MapProps>,
  pinAddress: __esri.AddressCandidate,
  locationUpdate: (locationOfWaste: WasteLocation) => void,
) => {
  mapView.graphics.removeAll();
  locationUpdate({
    address_line_1: pinAddress.attributes.PlaceName,
    address_line_2: pinAddress.attributes.Address,
    town_city: pinAddress.attributes.City,
    county: pinAddress.attributes.Region,
    postcode: pinAddress.attributes.Postcode,
    country: pinAddress.attributes.CntryName,
    UPRN: pinAddress.attributes.UPRN,
    USRN: pinAddress.attributes.USRN,
    latitude: String(pinAddress.location.latitude),
    longitude: String(pinAddress.location.longitude),
    officialAddress: pinAddress.attributes.OfficialLAAddress,
  });
  const mapMarker = pointGraphic(coordinates);
  dropMarkerOnMap(mapView, mapMarker, pinAddress);
};

export const handleMobileGeolocationEvent = (
  mapView: __esri.MapView,
  locationUpdate: (locationOfWaste: WasteLocation) => void,
) => {
  const geoLocationButton = document.getElementsByClassName(
    "esri-search__suggestions-list--current-location",
  )[0];
  if (geoLocationButton) {
    geoLocationButton.addEventListener("click", () => {
      if ("geolocation" in navigator && isMobile) {
        navigator.geolocation.getCurrentPosition(async function (position) {
          const latitude = position.coords.latitude;
          const longitude = position.coords.longitude;
          locationUpdate({
            ...emptyLocation,
            latitude: String(latitude),
            longitude: String(longitude),
          });
          await updateMapGivenLatLong(
            latitude,
            longitude,
            mapView,
            locationUpdate,
          );
        });
      }
    });
  }
};

function shimmer() {
  // create shimmer container
  const container = document.createElement("div");
  const child = document.createElement("div");
  container.appendChild(child);
  // create stylesheet
  const styleEl = document.createElement("style");
  document.head.appendChild(styleEl);
  const styleSheet = styleEl.sheet as CSSStyleSheet;
  const keyFrames = `@keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }`;
  styleSheet.insertRule(keyFrames, styleSheet.cssRules.length);
  container.style.cssText = `
    width: 100%;
    height: 38px;
    border-radius:5px;
    background-color: #ddd;
    position:relative;
    overflow:hidden;
  `;
  child.style.cssText = `
    width: 100%;
    height: 100%;
    background: linear-gradient(to right, #ddd 0%, #f5f5f5 50%, #ddd 100%);
    background-repeat: no-repeat;
    background-size: 200% 100%;
    animation: loading 1.0s infinite;
    position:absolute;
  `;
  return container;
}

export const loadingPopup = (event: __esri.ViewClickEvent) => ({
  title: `Loading...`,
  location: event.mapPoint,
  content: shimmer(),
});

export const outsideBoroughPopup = (event: __esri.ViewClickEvent) => ({
  title: `Error`,
  content: `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>You selected a location outside the borough, try again</span></div>`,
  location: event.mapPoint,
});

export const errorZoomInPopup = (event: __esri.ViewClickEvent) => ({
  title: `Error`,
  content: `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>Zoom in closer to select a accurate location</span></div>`,
  location: event.mapPoint,
});

export const geolocationOutOfBoroughPopup = () => {
  const point = new Point({
    longitude: initialCenterWCC[0],
    latitude: initialCenterWCC[1],
  });
  return {
    title: `Error`,
    content: `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>You must select somewhere inside the borough.</span></div>`,
    location: point,
  };
};

/**
 * The error message is specified in the story, refer to here: {@link https://westminstergov.atlassian.net/browse/RITE-1248}
 */
export const addressErrorPopup = (event: __esri.ViewClickEvent) => ({
  title: `Error`,
  content: `<div style="background-color: #FFCCCC; padding: 10px; border-radius: 5px;">
            <span>We cannot retrieve an address and may not provide waste related services for this location. If you want to try again, please select a different location.</span></div>`,
  location: event.mapPoint,
});

export const isMapCorrectZoomLevel = (mapView: __esri.MapView) =>
  mapView.zoom > 16;

export const isCoordinatesInsideBorough = async (
  coordinates: __esri.ViewClickEvent | NonNullable<MapProps>,
) => {
  let query;
  if (Array.isArray(coordinates)) {
    query = layerQuery([coordinates[0], coordinates[1]]);
  } else {
    query = layerQuery([
      coordinates.mapPoint.longitude,
      coordinates.mapPoint.latitude,
    ]);
  }
  // @ts-expect-error "type" is required in the geometry object but it is not in the type definition
  const response = await boundaryLayer.queryFeatures(query);
  const isMarkerInsideBorough = response.features.length > 0;
  return isMarkerInsideBorough;
};

export const getAddressCandidateFromMapPoint = async (
  point: __esri.Point,
): Promise<__esri.AddressCandidate | null> => {
  let address = null;
  await locationToAddress(
    GEOCODE_SERVER,
    {
      location: point,
      // @ts-expect-error "wkid" is the only required property in the spatialReference object to output the correct coordinates
      outSpatialReference: { wkid: 4326 },
    },
    500,
  )
    .then((response) => {
      address = response;
    })
    .catch(() => {
      address = null;
    });
  return address;
};

type addressType = {
  key: string | null;
  text: string;
  sourceIndex: number | null;
  LAT: string;
  LNG: string;
  address_line_1: string;
  address_line_2: string;
  town_city: string;
  county: string;
  postcode: string;
  country: string;
  UPRN: string;
  USRN: string;
};

type searchResultType = {
  location: null;
  text: string;
  sourceIndex: null;
  key: null;
};

type geolocationType = { location: Point };
type addressResponseType = {
  address_line_1: string;
  address_line_2: string;
  town: string;
  county: string;
  postcode: string;
  country: string;
  UPRN: string;
  USRN: string;
  latitude: string;
  longitude: string;
  full_address: string;
};

type addressResponseType2 = {
  view: __esri.MapView;
  sourceIndex: number;
  location: null;
  suggestResult: addressType | searchResultType | geolocationType;
  spatialReference: __esri.SpatialReference;
  exactMatch: boolean;
  maxResults: number;
};

interface ExtendedGetResultsHandler extends __esri.GetResultsHandler {
  suggestTerm: string;
  sourceIndex: number;
}

export const addressBaseSearchSource = (
  view: __esri.MapView,
  locationUpdate: (locationOfWaste: WasteLocation) => void,
) => {
  const handleGetSuggestions = async (params: ExtendedGetResultsHandler) => {
    view.popup.close();
    const requestUrl = `${LOCATION_ENDPOINT}?query=${encodeURIComponent(
      params.suggestTerm,
    )}&fq=LOCAL_CUSTODIAN_CODE:5990`;
    return await axios
      .get(requestUrl, { headers: { "Ocp-Apim-Subscription-Key": apiKey } })
      .then((response) => {
        if (
          response.data.results === undefined ||
          response.data.results.length === 0
        ) {
          console.warn("No suggestions found");
          return [];
        }
        return response.data.results
          .map((item: addressResponseType) => {
            return {
              key: item.UPRN,
              text: item.full_address,
              sourceIndex: params.sourceIndex,
              LAT: item.latitude,
              LNG: item.longitude,
              address_line_1: item.address_line_1,
              address_line_2: item.address_line_2,
              town_city: item.town,
              county: item.county,
              postcode: item.postcode,
              country: item.country,
              UPRN: item.UPRN,
              USRN: item.USRN,
            };
          })
          .filter((suggestion: addressType) => suggestion != null);
      })
      .catch((error) => {
        Sentry.captureException(error);
        return [];
      });
  };

  const handleGetResults = async (params: addressResponseType2) => {
    view.graphics.removeAll();
    view.closePopup();
    let response = {
      address_line_1: "",
      address_line_2: "",
      town: "",
      county: "",
      postcode: "",
      country: "",
      UPRN: "",
      USRN: "",
      latitude: "",
      longitude: "",
      full_address: "",
    };

    let result = params.suggestResult;
    // Handles search from "Enter" key press on keyboard or "Magnifying glass" button click
    if (
      (result as searchResultType).location === null &&
      (result as searchResultType).key === null
    ) {
      const userSearchQueryText = (result as searchResultType).text;
      const requestUrl = `${LOCATION_ENDPOINT}?query=${encodeURIComponent(
        userSearchQueryText,
      )}&fq=LOCAL_CUSTODIAN_CODE:5990&maxresults=1`;
      const request = await axios
        .get(requestUrl, {
          headers: { "Ocp-Apim-Subscription-Key": apiKey },
        })
        .then((response) => {
          return response.data.results[0];
        })
        .catch((error) => {
          Sentry.captureException(error);
          return null;
        });
      if (request) {
        response = { ...request };
      }
    }
    // Handles search result from Geolocation provided by user
    else if (
      (params.suggestResult as geolocationType).location instanceof Point
    ) {
      result = params.suggestResult as geolocationType;
      const requestUrl = `${NEAREST_ENDPOINT}?point=${encodeURIComponent(
        `${result.location.latitude},${result.location.longitude}`,
      )}&fq=LOCAL_CUSTODIAN_CODE:5990`;
      const request = await axios
        .get(requestUrl, {
          headers: { "Ocp-Apim-Subscription-Key": apiKey },
        })
        .then(async (response) => {
          // error popup if no address found (out of borough) - uses center of map as location for pin drop
          if (response.data.results.length === 0) {
            view.zoom = 13;
            view.center = new Point({
              longitude: initialCenterWCC[0],
              latitude: initialCenterWCC[1],
            });
            view.popup.open(geolocationOutOfBoroughPopup());
            return null;
          }
          return response.data.results[0];
        })
        .catch((error) => {
          Sentry.captureException(error);
          return null;
        });
      if (request) {
        response = { ...request };
      }
    }
    // Handles search result from Suggestions List
    else {
      result = params.suggestResult as addressType;
      response = {
        full_address: result.text,
        latitude: result.LAT,
        longitude: result.LNG,
        UPRN: result.UPRN,
        USRN: result.USRN,
        town: result.town_city,
        county: result.county,
        postcode: result.postcode,
        country: result.country,
        address_line_1: result.address_line_1,
        address_line_2: result.address_line_2,
      };
    }

    if (
      response.latitude !== null &&
      response.longitude !== null &&
      response.UPRN !== null
    ) {
      const point = new Point({
        longitude: Number.parseFloat(response.longitude),
        latitude: Number.parseFloat(response.latitude),
      });
      const buffer = geometryEngine.geodesicBuffer(
        point,
        100,
        "meters",
      ) as __esri.Polygon;
      const searchResult = {
        extent: buffer.extent,
        feature: new Graphic({
          geometry: point,
          attributes: {
            Address: response.full_address,
            UPRN: response.UPRN,
          },
        }),
        label: response.UPRN + response.full_address,
        name: response.full_address,
      };
      locationUpdate({
        address_line_1: response.address_line_1,
        address_line_2: response.address_line_2,
        town_city: response.town,
        county: response.county,
        postcode: response.postcode,
        country: response.country,
        UPRN: response.UPRN,
        USRN: response.USRN,
        latitude: String(response.latitude),
        longitude: String(response.longitude),
        officialAddress: response.full_address,
      });

      return [searchResult] as unknown as __esri.SearchResult[];
    } else {
      return [] as unknown as __esri.SearchResult[];
    }
  };

  return new SearchSource({
    placeholder: "Search for an address",
    resultSymbol: markerSymbol,
    maxResults: 10,
    getSuggestions: handleGetSuggestions,
    getResults: handleGetResults,
  });
};
